import React, { useState, useRef, useEffect } from "react";

/**
 * Reusable photo upload component.
 * Props:
 *   currentUrl   — existing photo URL to show (or null)
 *   onUploaded   — called with { id, url } after successful upload
 *   uploadUrl    — POST endpoint for the upload
 *   size         — "sm" | "lg" (default "sm")
 */
export default function PhotoUpload({ currentUrl, onUploaded, uploadUrl, size = "sm" }) {
  const [preview, setPreview] = useState(currentUrl || null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef();

  // Sync when parent updates currentUrl (e.g. after library pick)
  useEffect(() => {
    setPreview(currentUrl || null);
  }, [currentUrl]);

  const dim = size === "lg" ? 128 : 96;

  async function handleFile(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please upload an image file.");
      return;
    }
    setUploading(true);
    setError(null);

    // Local preview immediately
    const reader = new FileReader();
    reader.onload = e => setPreview(e.target.result);
    reader.readAsDataURL(file);

    const form = new FormData();
    form.append("file", file);

    try {
      const res = await fetch(uploadUrl, { method: "POST", body: form });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Upload failed: ${res.status}`);
      }
      const data = await res.json();
      setPreview(data.url);
      onUploaded(data);
    } catch (e) {
      setError(e.message);
      setPreview(currentUrl || null);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "var(--space-2)" }}>
      <div
        style={{
          width: dim, height: dim,
          borderRadius: "var(--radius-md)",
          background: preview ? "transparent" : "var(--color-bg-inset)",
          border: preview ? "none" : "2px dashed var(--color-border)",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", overflow: "hidden", flexShrink: 0,
          position: "relative",
        }}
        onClick={() => !uploading && inputRef.current.click()}
        title="Click to upload photo"
      >
        {preview ? (
          <img src={preview} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
        ) : (
          <span style={{ fontSize: size === "lg" ? 36 : 24, color: "var(--color-text-disabled)" }}>+</span>
        )}
        {uploading && (
          <div style={{
            position: "absolute", inset: 0,
            background: "rgba(255,255,255,0.7)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 11,
          }}>
            …
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={e => handleFile(e.target.files[0])}
      />
      {error && (
        <p style={{ fontSize: "var(--text-xs)", color: "var(--color-critical)", textAlign: "center" }}>
          {error}
        </p>
      )}
    </div>
  );
}
